import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { IDefaultResponse } from "../../models/DefaultResponse";
import { IEbayPart } from "../../models/EbayPart/EbayPart";
import { useEffect } from "react";

export interface IUsePartsProps {
    hideCategorised?: boolean;
    hideAccepted?: boolean;
    limit?: number;
}

export default function useParts({
    hideCategorised,
    hideAccepted, 
    limit
}: IUsePartsProps) {
    const { get, post, patch } = useApiHelper();
    const queryClient = useQueryClient();

    useEffect(() => {
        // refetch data when any of the filters change
        parts.refetch();
    }, [hideCategorised, hideAccepted, limit]);

    const parts = useQuery({
        queryKey: ["parts", hideCategorised, hideAccepted, limit],
        queryFn: () => {
            // props to query string
            const params: Record<string, string> = {};
            if (hideCategorised) params.hideCategorised = "true";
            if (hideAccepted) params.hideAccepted = "true";
            if (limit) params.limit = limit.toString();
            const queryString = new URLSearchParams(params).toString();
            return get<IDefaultResponse<IEbayPart[]>>(`/part?${queryString}`).then((res) => res.data);
        },
        enabled: limit !== undefined, // only run if limit is defined
    });

    const totals = useQuery({
        queryKey: ["parts", "totals"],
        queryFn: () =>  get<IDefaultResponse<any>>(`/part/totals`).then((res) => res.data),
    });

    const assignCategory = useMutation({
        mutationFn: (data: any) => post(`/part/category/assign`, data),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["parts"] });          
        },
      });

    const acceptCategory = useMutation({
        mutationFn: (data: any) => post(`/part/category/accept`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["parts"] });
        },
    });

    const archive = useMutation({
        mutationFn: (data: any) => post(`/part/category/archive`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["parts"] });
        },
    });

    const updatePart = useMutation({
        mutationFn: (data: any) => patch(`/part`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["parts"] });
        },
    });

    const markReviewed = useMutation({
        mutationFn: (data: any) => post(`/part/category/reviewed`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["parts"] });
        },
    });

    return {
        parts,
        totals,
        assignCategory,
        acceptCategory,
        archive,
        updatePart,
        markReviewed
    };
}